<div class="afm-container" (document:click)="$event">

  <div *ngIf="showPageLoader" class="afm-payment-loader">
    <div class="afm-payment-loader-card">
      <div class="afm-spinner"></div>
      <p>Processing payment...</p>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════
       SCHOOL LOADING
  ════════════════════════════════════════════════ -->
  <div *ngIf="isSchoolLoading" class="afm-page-loader">
    <div class="afm-spinner"></div>
    <p>Initialising fee management…</p>
  </div>

  <ng-container *ngIf="!isSchoolLoading">

    <!-- ── PAGE HEADER ────────────────────────────── -->
    <div class="afm-page-header">
      <div class="afm-page-title-row">
        <button *ngIf="activeTab === 'students' && currentLevel !== 'classes'" class="afm-back-btn" (click)="goBack()">
          <i class="bi bi-arrow-left"></i>
        </button>

        <div class="afm-page-title">
          <i class="bi bi-cash-stack me-2"></i>
          Fee Management
          <span *ngIf="schoolName" class="afm-school-tag">{{ schoolName }}</span>
        </div>
      </div>

      <!-- BREADCRUMB (only relevant on 'students' tab) -->
      <nav *ngIf="activeTab === 'students'" class="afm-breadcrumb">
        <span
          class="afm-bc-item"
          [class.afm-bc-active]="currentLevel === 'classes'"
          (click)="goToClasses()">
          <i class="bi bi-grid-3x3-gap-fill me-1"></i>Classes
        </span>

        <ng-container *ngIf="selectedClass">
          <i class="bi bi-chevron-right afm-bc-sep"></i>
          <span
            class="afm-bc-item"
            [class.afm-bc-active]="currentLevel === 'sections'"
            (click)="goToSections()">
            {{ selectedClass.className }}
          </span>
        </ng-container>

        <ng-container *ngIf="currentLevel === 'students' || currentLevel === 'feeDetail'">
          <i class="bi bi-chevron-right afm-bc-sep"></i>
          <span
            class="afm-bc-item"
            [class.afm-bc-active]="currentLevel === 'students'"
            (click)="goToStudents()">
            {{ selectedSection ? selectedSection.sectionName : 'All Students' }}
          </span>
        </ng-container>

        <ng-container *ngIf="currentLevel === 'feeDetail' && selectedStudentFeeView">
          <i class="bi bi-chevron-right afm-bc-sep"></i>
          <span class="afm-bc-item afm-bc-active">
            {{ selectedStudentFeeView.student.firstName }}
            {{ selectedStudentFeeView.student.lastName }}
          </span>
        </ng-container>
      </nav>
    </div>

    <!-- ── TAB BAR ────────────────────────────────────── -->
    <div class="afm-tabs-container">
      <div class="afm-tabs-wrapper">
        <button
          type="button"
          class="afm-tab-item"
          [class.afm-tab-item-active]="activeTab === 'overview'"
          (click)="switchTab('overview')">
          <i class="bi bi-pie-chart-fill afm-tab-icon"></i>
          <span>Overview &amp; Analytics</span>
        </button>

        <button
          type="button"
          class="afm-tab-item"
          [class.afm-tab-item-active]="activeTab === 'students'"
          (click)="switchTab('students')">
          <i class="bi bi-wallet2 afm-tab-icon"></i>
          <span>Collect Fees</span>
        </button>

        <button
          type="button"
          class="afm-tab-item"
          [class.afm-tab-item-active]="activeTab === 'receipts'"
          (click)="switchTab('receipts')">
          <i class="bi bi-receipt-cutoff afm-tab-icon"></i>
          <span>Payment Receipts</span>
          <span *ngIf="sessionReceiptsList.length > 0" class="afm-tab-pill">
            {{ sessionReceiptsList.length }}
          </span>
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════
         TAB 1 — OVERVIEW & ANALYTICS
    ════════════════════════════════════════════════ -->
    <ng-container *ngIf="activeTab === 'overview'">
      <div class="afm-overview-container">

        <!-- Top Header & Actions -->
        <div class="afm-overview-topbar">
          <div class="afm-overview-heading">
            <h4>Fee Collection Overview</h4>
            <p>Class-wise pending and collected fee summary for this school</p>
          </div>

          <div class="afm-overview-actions">
            <button
              type="button"
              class="afm-btn-refresh"
              [disabled]="isOverviewLoading"
              (click)="refreshOverviewData()"
              title="Refresh overview data">
              <i class="bi bi-arrow-clockwise" [class.afm-spin]="isOverviewLoading"></i>
              <span>Refresh</span>
            </button>
          </div>
        </div>


        <!-- Metric Cards -->
        <div class="afm-overview-metrics">
          <!-- Card 1: Collected -->
          <div class="afm-metric-card afm-metric-collected">
            <div class="afm-metric-icon">
              <i class="bi bi-check-circle-fill"></i>
            </div>
            <div class="afm-metric-info">
              <span class="afm-metric-title">Total Collected</span>
              <div class="afm-metric-amount">
                {{ overviewTotalCollected | currency:'INR':'symbol':'1.0-2' }}
              </div>
              <div class="afm-metric-sub afm-sub-positive">
                <i class="bi bi-graph-up-arrow me-1"></i>
                {{ overviewCollectionPercent }}% of expected
              </div>
            </div>
          </div>

          <!-- Card 2: Pending -->
          <div class="afm-metric-card afm-metric-pending">
            <div class="afm-metric-icon">
              <i class="bi bi-hourglass-split"></i>
            </div>
            <div class="afm-metric-info">
              <span class="afm-metric-title">Total Pending</span>
              <div class="afm-metric-amount">
                {{ overviewTotalPending | currency:'INR':'symbol':'1.0-2' }}
              </div>
              <div class="afm-metric-sub afm-sub-warning">
                <i class="bi bi-exclamation-circle me-1"></i>
                Outstanding balance
              </div>
            </div>
          </div>

          <!-- Card 3: Expected -->
          <div class="afm-metric-card afm-metric-expected">
            <div class="afm-metric-icon">
              <i class="bi bi-cash-stack"></i>
            </div>
            <div class="afm-metric-info">
              <span class="afm-metric-title">Total Expected</span>
              <div class="afm-metric-amount">
                {{ overviewTotalExpected | currency:'INR':'symbol':'1.0-2' }}
              </div>
              <div class="afm-metric-sub">
                <i class="bi bi-calendar3 me-1"></i>
                Across all classes
              </div>
            </div>
          </div>

          <!-- Card 4: Overall Progress -->
          <div class="afm-metric-card afm-metric-progress">
            <div class="afm-metric-icon">
              <i class="bi bi-speedometer2"></i>
            </div>
            <div class="afm-metric-info">
              <span class="afm-metric-title">Collection Rate</span>
              <div class="afm-metric-amount">
                {{ overviewCollectionPercent }}%
              </div>
              <div class="afm-metric-bar-wrap">
                <div class="afm-metric-bar-fill" [style.width.%]="overviewCollectionPercent"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Class Breakdown Section -->
        <div class="afm-overview-section">
          <div class="afm-overview-section-header">
            <div class="afm-sec-title">
              <i class="bi bi-mortarboard-fill me-2"></i>
              Class-wise Collection Status
            </div>
            <span class="afm-count-badge">{{ overviewFeeList.length }} Classes</span>
          </div>

          <!-- Loading state -->
          <div *ngIf="isOverviewLoading" class="afm-overview-loading">
            <div class="afm-spinner"></div>
            <span>Loading class collection figures…</span>
          </div>

          <!-- Empty state -->
          <div *ngIf="!isOverviewLoading && overviewFeeList.length === 0" class="afm-empty-state">
            <i class="bi bi-clipboard-data"></i>
            <p>No class fee data available for this school.</p>
            <button type="button" class="afm-btn-refresh mt-2" (click)="refreshOverviewData()">
              <i class="bi bi-arrow-clockwise me-1"></i> Check Again
            </button>
          </div>

          <!-- Class List Table -->
          <div *ngIf="!isOverviewLoading && overviewFeeList.length > 0" class="afm-overview-table-wrap">
            <table class="afm-overview-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th class="text-end">Collected</th>
                  <th class="text-end">Pending</th>
                  <th class="text-end">Total</th>
                  <th style="min-width: 140px;">Collection Progress</th>
                  <th class="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of overviewFeeList">
                  <td class="afm-table-classname">
                    <i class="bi bi-mortarboard me-2 text-muted"></i>
                    <strong>{{ item.className || ('Class #' + item.classId) }}</strong>
                  </td>
                  <td class="text-end afm-text-collected">
                    {{ (item.paidFeeAmount || 0) | currency:'INR':'symbol':'1.0-2' }}
                  </td>
                  <td class="text-end afm-text-pending">
                    {{ (item.pendingFeeAmount || 0) | currency:'INR':'symbol':'1.0-2' }}
                  </td>
                  <td class="text-end afm-text-total">
                    {{ getOverviewClassTotal(item) | currency:'INR':'symbol':'1.0-2' }}
                  </td>
                  <td>
                    <div class="afm-tbl-progress-wrap">
                      <div class="afm-tbl-progress-bar">
                        <div
                          class="afm-tbl-progress-fill"
                          [style.width.%]="getOverviewClassPercent(item)">
                        </div>
                      </div>
                      <span class="afm-tbl-progress-pct">{{ getOverviewClassPercent(item) }}%</span>
                    </div>
                  </td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="afm-btn-action-collect"
                      (click)="selectClassFromOverview(item)"
                      title="Collect fees for this class">
                      <span>Collect</span>
                      <i class="bi bi-arrow-right-short ms-1"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </ng-container>

    <!-- ═══════════════════════════════════════════════
         TAB 2 — RECEIPTS
    ════════════════════════════════════════════════ -->
    <ng-container *ngIf="activeTab === 'receipts'">
      <div class="afm-receipts-container">

        <!-- Top Header & Search -->
        <div class="afm-receipts-topbar">
          <div class="afm-receipts-heading">
            <h4>Fee Payment Receipts</h4>
            <p>Bills and receipts generated during this session</p>
          </div>

          <div class="afm-receipts-search-wrap">
            <i class="bi bi-search afm-search-icon"></i>
            <input
              type="text"
              class="afm-search-input"
              placeholder="Search receipt no, student, class..."
              [(ngModel)]="receiptSearchTerm" />
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="filteredReceiptsList.length === 0" class="afm-empty-state">
          <i class="bi bi-receipt"></i>
          <p *ngIf="sessionReceiptsList.length === 0">No receipts have been generated in this session yet.</p>
          <p *ngIf="sessionReceiptsList.length > 0">No receipts matched your search query.</p>
          <button
            *ngIf="sessionReceiptsList.length === 0"
            type="button"
            class="afm-btn-action-collect mt-2"
            (click)="switchTab('students')">
            <i class="bi bi-wallet2 me-1"></i> Go to Fee Collection
          </button>
        </div>

        <!-- Receipts Table -->
        <div *ngIf="filteredReceiptsList.length > 0" class="afm-overview-table-wrap">
          <table class="afm-overview-table">
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Student</th>
                <th>Class / Section</th>
                <th>Fee Details</th>
                <th class="text-end">Amount Paid</th>
                <th class="text-center">Mode</th>
                <th>Date</th>
                <th class="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let bill of filteredReceiptsList">
                <td>
                  <span class="afm-receipt-badge">
                    <i class="bi bi-receipt me-1"></i>{{ bill.receiptNo || ('#' + bill.invoiceId) }}
                  </span>
                </td>
                <td>
                  <strong>{{ bill.studentName }}</strong>
                </td>
                <td>
                  <span class="afm-class-tag">{{ bill.className }} - {{ bill.sectionName }}</span>
                </td>
                <td>
                  <div>{{ bill.feeName }}</div>
                  <small class="text-muted" *ngIf="bill.installmentName">{{ bill.installmentName }}</small>
                </td>
                <td class="text-end afm-text-collected fw-bold">
                  {{ (bill.paidAmount || bill.amount) | currency:'INR':'symbol':'1.0-2' }}
                </td>
                <td class="text-center">
                  <span
                    class="afm-payment-mode-chip"
                    [class.afm-payment-mode-cash]="(bill.paymentMethod || '').toLowerCase().includes('cash')"
                    [class.afm-payment-mode-online]="!(bill.paymentMethod || '').toLowerCase().includes('cash')">
                    {{ bill.paymentMethod || 'Paid' }}
                  </span>
                </td>
                <td>
                  <small>{{ getBillDateText(bill) }}</small>
                </td>
                <td class="text-center">
                  <button
                    type="button"
                    class="afm-btn-view-bill"
                    (click)="openReceiptBill(bill, feeBillModal)"
                    title="View &amp; Print Bill">
                    <i class="bi bi-printer me-1"></i>
                    <span>View Bill</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </ng-container>

    <!-- ═══════════════════════════════════════════════
         TAB 3 — COLLECT FEES (STUDENTS WORKFLOW)
    ════════════════════════════════════════════════ -->
    <ng-container *ngIf="activeTab === 'students'">

      <!-- ═══════════════════════════════════════════════
           LEVEL 1 — CLASS LISTING
      ════════════════════════════════════════════════ -->
      <ng-container *ngIf="currentLevel === 'classes'">

      <!-- Search -->
      <div class="afm-toolbar">
        <div class="afm-search-wrap">
          <i class="bi bi-search afm-search-icon"></i>
          <input
            type="text"
            class="afm-search-input"
            placeholder="Search classes…"
            [(ngModel)]="classSearchTerm" />
        </div>

        <span class="afm-count-badge">{{ filteredClasses.length }} classes</span>
      </div>

      <!-- Loading -->
      <div *ngIf="isClassLoading" class="afm-loader-row">
        <div class="afm-spinner"></div>
        <span>Loading classes…</span>
      </div>

      <!-- Empty -->
      <div *ngIf="!isClassLoading && filteredClasses.length === 0" class="afm-empty-state">
        <i class="bi bi-building"></i>
        <h6>No Classes Found</h6>
        <p>No classes have been set up for this school yet.</p>
      </div>

      <!-- Class Grid -->
      <div *ngIf="!isClassLoading && filteredClasses.length > 0" class="afm-class-grid">
        <div
          *ngFor="let cls of filteredClasses; trackBy: trackByClassId"
          class="afm-class-card"
          (click)="selectClass(cls)">

          <div class="afm-class-avatar">
            <i class="bi bi-mortarboard-fill"></i>
          </div>

          <div class="afm-class-info">
            <div class="afm-class-name">{{ cls.className }}</div>
            <div class="afm-class-desc">{{ cls.description || 'Click to manage fees' }}</div>
            <div *ngIf="getClassFeeOverview(cls.classId) as feeInfo" class="afm-class-fee-pills">
              <span class="afm-fee-pill afm-fee-pill-collected" title="Collected Fees">
                <i class="bi bi-check-circle-fill me-1"></i>{{ (feeInfo.paidFeeAmount || 0) | currency:'INR':'symbol':'1.0-0' }}
              </span>
              <span class="afm-fee-pill afm-fee-pill-pending" title="Pending Fees">
                <i class="bi bi-clock-fill me-1"></i>{{ (feeInfo.pendingFeeAmount || 0) | currency:'INR':'symbol':'1.0-0' }}
              </span>
            </div>
          </div>

          <div class="afm-class-arrow">
            <i class="bi bi-chevron-right"></i>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ═══════════════════════════════════════════════
         LEVEL 2 — SECTIONS
    ════════════════════════════════════════════════ -->
    <ng-container *ngIf="currentLevel === 'sections' && !noSectionsFallback">

      <div *ngIf="isSectionLoading" class="afm-loader-row">
        <div class="afm-spinner"></div>
        <span>Loading sections…</span>
      </div>

      <ng-container *ngIf="!isSectionLoading">

        <!-- Class summary bar -->
        <div class="afm-level-header">
          <div class="afm-lh-title">
            <i class="bi bi-diagram-3 me-2"></i>
            Sections — {{ selectedClass?.className }}
          </div>
        </div>

        <!-- Section Cards -->
        <div class="afm-section-grid">
          <div class="afm-section-card afm-section-all" (click)="selectAllStudents()">
            <div class="afm-sec-icon">
              <i class="bi bi-people-fill"></i>
            </div>
            <div class="afm-sec-label">All Students</div>
            <div class="afm-sec-sub">View all without section filter</div>
          </div>

          <div
            *ngFor="let sec of sectionList; trackBy: trackBySectionId"
            class="afm-section-card"
            (click)="selectSection(sec)">
            <div class="afm-sec-icon">
              <i class="bi bi-person-workspace"></i>
            </div>
            <div class="afm-sec-label">{{ sec.sectionName }}</div>
            <div class="afm-sec-sub">Section</div>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- ═══════════════════════════════════════════════
         LEVEL 3 — STUDENT LISTING
    ════════════════════════════════════════════════ -->
    <ng-container *ngIf="currentLevel === 'students'">

      <div class="afm-level-header">
        <div class="afm-lh-title">
          <i class="bi bi-people me-2"></i>
          {{ selectedSection ? selectedSection.sectionName + ' — ' : '' }}{{ selectedClass?.className }}
          <span class="afm-count-pill">{{ filteredStudents.length }}</span>
        </div>

        <div class="afm-lh-actions">
          <div class="afm-search-wrap">
            <i class="bi bi-search afm-search-icon"></i>
            <input
              type="text"
              class="afm-search-input"
              placeholder="Search students…"
              [(ngModel)]="studentSearchTerm" />
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div *ngIf="isStudentLoading" class="afm-loader-row">
        <div class="afm-spinner"></div>
        <span>Loading students…</span>
      </div>

      <!-- Empty -->
      <div *ngIf="!isStudentLoading && filteredStudents.length === 0" class="afm-empty-state">
        <i class="bi bi-person-x"></i>
        <h6>No Students Found</h6>
        <p>No students are mapped to this class/section.</p>
      </div>

      <!-- Student Table -->
      <div *ngIf="!isStudentLoading && filteredStudents.length > 0" class="afm-student-table-wrap">
        <div class="afm-student-table-scroll">
          <table class="afm-student-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Contact</th>
                <th>Section</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr
                *ngFor="let stu of filteredStudents; trackBy: trackByStudentId"
                class="afm-stu-row">

                <td>
                  <div class="afm-stu-identity">
                    <div class="afm-stu-avatar">{{ getStudentInitials(stu) }}</div>
                    <div>
                      <div class="afm-stu-name">
                        {{ stu.firstName }} {{ stu.middleName }} {{ stu.lastName }}
                      </div>
                      <div class="afm-stu-email">{{ stu.emailId || '—' }}</div>
                    </div>
                  </div>
                </td>

                <td class="afm-stu-mobile">{{ stu.mobileNo || '—' }}</td>

                <td>
                  <span class="afm-sec-chip" *ngIf="stu.sectionName">
                    {{ stu.sectionName }}
                  </span>
                  <span *ngIf="!stu.sectionName" class="afm-muted">—</span>
                </td>

                <td class="text-center">
                  <div class="afm-action-group">
                    <button
                      type="button"
                      class="afm-btn-fee"
                      (click)="openStudentFeeDetail(stu)"
                      title="View Fee Details">
                      <i class="bi bi-receipt me-1"></i> Fee Details
                    </button>

                    <button
                      type="button"
                      class="afm-btn-collect"
                      (click)="openFeeCollecting(stu)"
                      title="Collect Fees">
                      <i class="bi bi-cash-coin me-1"></i> Fees Collecting
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ng-container>

    <!-- ═══════════════════════════════════════════════
         LEVEL 4 — STUDENT FEE DETAIL
    ════════════════════════════════════════════════ -->
    <ng-container *ngIf="currentLevel === 'feeDetail'">

      <div *ngIf="isFeeDetailLoading" class="afm-loader-row">
        <div class="afm-spinner"></div>
        <span>Loading fee details…</span>
      </div>

      <ng-container *ngIf="!isFeeDetailLoading && selectedStudentFeeView">

        <!-- Student Info Bar -->
        <div class="afm-student-info-bar">
          <div class="afm-sib-avatar">
            {{ getStudentInitials(selectedStudentFeeView.student) }}
          </div>

          <div class="afm-sib-details">
            <div class="afm-sib-name">
              {{ selectedStudentFeeView.student.firstName }}
              {{ selectedStudentFeeView.student.middleName }}
              {{ selectedStudentFeeView.student.lastName }}
            </div>

            <div class="afm-sib-meta">
              <span>
                <i class="bi bi-envelope me-1"></i>
                {{ selectedStudentFeeView.student.emailId || '—' }}
              </span>
              <span>
                <i class="bi bi-phone me-1"></i>
                {{ selectedStudentFeeView.student.mobileNo || '—' }}
              </span>
              <span>
                <i class="bi bi-building me-1"></i>
                {{ selectedClass?.className }}
              </span>
              <span *ngIf="selectedSection">
                <i class="bi bi-diagram-3 me-1"></i>
                {{ selectedSection.sectionName }}
              </span>
            </div>
          </div>

          <div class="afm-sib-actions">

            <button
  type="button"
  class="afm-btn-edit-paid"
  *ngIf="selectedStudentFeeView.grandPaid > 0"
  (click)="openEditPaidAmountModal(selectedStudentFeeView, editPaidAmountModal)"
>
  <i class="bi bi-pencil-square me-1"></i>
  Edit Paid Amount
</button>

            <span *ngIf="isCollectingMode" class="afm-collecting-mode-label">
              <i class="bi bi-cash-coin me-1"></i>
              Fees Collecting Mode
            </span>

            <button
              type="button"
              class="afm-btn-notify"
              *ngIf="
                !selectedStudentFeeView.noFeeAssigned &&
                studentHasPendingFee(selectedStudentFeeView.student) &&
                !isCollectingMode
              "
              (click)="openSingleNotifyModal(selectedStudentFeeView.student, singleNotifyModal)">
              <i class="bi bi-send me-1"></i> Notify Parent
            </button>

            <span
              *ngIf="selectedStudentFeeView.noFeeAssigned"
              class="afm-no-fee-label">
              <i class="bi bi-journal-x me-1"></i>
              No Fee Structure Assigned
            </span>

            <span
              *ngIf="
                !selectedStudentFeeView.noFeeAssigned &&
                !studentHasPendingFee(selectedStudentFeeView.student)
              "
              class="afm-paid-label afm-paid-label-light">
              <i class="bi bi-check-circle-fill me-1"></i>
              All Fees Paid
            </span>
          </div>
        </div>

        <!-- No Fee Assigned -->
        <div *ngIf="selectedStudentFeeView.noFeeAssigned" class="afm-empty-state">
          <i class="bi bi-journal-x"></i>
          <h6>No Fee Records Found</h6>
          <p>No fee structure has been assigned to this student yet.</p>
        </div>

        <!-- Fee Ledger -->
        <ng-container *ngIf="!selectedStudentFeeView.noFeeAssigned">

          <div *ngIf="selectedStudentFeeView.isLoading" class="afm-loader-row">
            <div class="afm-spinner"></div>
            <span>Calculating payment status…</span>
          </div>

          <ng-container *ngIf="!selectedStudentFeeView.isLoading">

            <!-- Summary Strip -->
            <div class="nw-summary-strip">
              <div class="nw-summary-item">
                <span class="nw-si-label">Total Fee</span>
                <span class="nw-si-value">
                  {{ selectedStudentFeeView.grandTotal | currency:'INR':'symbol':'1.0-2' }}
                </span>
              </div>

              <div class="nw-summary-item nw-si-paid">
                <span class="nw-si-label">Paid</span>
                <span class="nw-si-value">
                  {{ selectedStudentFeeView.grandPaid | currency:'INR':'symbol':'1.0-2' }}
                </span>
              </div>

              <div class="nw-summary-item" [class.nw-si-due]="selectedStudentFeeView.grandDue > 0">
                <span class="nw-si-label">Outstanding</span>
                <span class="nw-si-value">
                  {{ selectedStudentFeeView.grandDue | currency:'INR':'symbol':'1.0-2' }}
                </span>
              </div>

              <div class="nw-summary-progress">
                <div class="nw-sp-header">
                  <span>Payment Progress</span>
                  <span class="nw-sp-pct">{{ selectedStudentFeeView.progressPercent }}%</span>
                </div>
                <div class="nw-sp-track">
                  <div
                    class="nw-sp-fill"
                    [style.width.%]="selectedStudentFeeView.progressPercent">
                  </div>
                </div>
              </div>
            </div>

            <!-- Collection Helper Bar -->
            <div
              *ngIf="isCollectingMode && selectedStudentFeeView.grandDue > 0"
              class="afm-collection-pay-strip">

              <div class="afm-collection-left">
                <div class="afm-collection-title">
                  <i class="bi bi-wallet2 me-1"></i>
                  Collect Fees
                </div>
                <div class="afm-collection-sub">
                  Select installment, enter amount, and continue payment collection.
                </div>
              </div>

              <div class="afm-collection-actions">
                <button
                  type="button"
                  class="afm-btn-select-all"
                  (click)="selectAllPendingInstallments(selectedStudentFeeView)">
                  <i class="bi bi-check2-square me-1"></i>
                  Select All Pending
                </button>

                <button
                  type="button"
                  class="afm-btn-clear-selected"
                  (click)="clearSelectedInstallments()"
                  [disabled]="getSelectedCollectionCount() === 0">
                  Clear
                </button>
              </div>
            </div>

            <!-- Fee type tabs: original page header and payment grid stay unchanged. -->
            <div class="d-flex align-items-center gap-2 mb-3"
              *ngIf="selectedStudentFeeView.mandatoryInstallmentRows.length > 0 || selectedStudentFeeView.feeGroups.length > 0">
              <button type="button"
                *ngIf="selectedStudentFeeView.mandatoryInstallmentRows.length > 0"
                class="btn btn-sm"
                [style.background]="getEffectiveFeeDisplayTab(selectedStudentFeeView) === 'mandatory' ? '#011044' : '#ffffff'"
                [style.color]="getEffectiveFeeDisplayTab(selectedStudentFeeView) === 'mandatory' ? '#ffffff' : '#011044'"
                style="border:1px solid #011044;border-radius:7px;padding:6px 14px;"
                (click)="switchFeeDisplayTab('mandatory')">
                Mandatory Fees ({{ selectedStudentFeeView.mandatoryInstallmentRows.length }} Installments)
              </button>
              <button type="button"
                *ngIf="selectedStudentFeeView.feeGroups.length > 0"
                class="btn btn-sm"
                [style.background]="getEffectiveFeeDisplayTab(selectedStudentFeeView) === 'other' ? '#011044' : '#ffffff'"
                [style.color]="getEffectiveFeeDisplayTab(selectedStudentFeeView) === 'other' ? '#ffffff' : '#011044'"
                style="border:1px solid #011044;border-radius:7px;padding:6px 14px;"
                (click)="switchFeeDisplayTab('other')">
                Other Fees
              </button>
            </div>

            <!-- Combined Mandatory Fees: one payable installment, expandable real charge details -->
            <div class="nw-groups" *ngIf="selectedStudentFeeView.mandatoryInstallmentRows.length > 0 && getEffectiveFeeDisplayTab(selectedStudentFeeView) === 'mandatory'">
              <div class="nw-group">
                <div class="nw-group-header">
                  <span class="nw-group-title">
                    <i class="bi bi-folder2-open me-2"></i>
                    Mandatory Fees
                  </span>
                  <span class="nw-group-total">
                    {{ getMandatoryFeeTotal(selectedStudentFeeView) | currency:'INR':'symbol':'1.0-2' }}
                  </span>
                </div>

                <div class="nw-fee-cards">
                  <div class="nw-fee-card">
                    <div class="nw-inst-wrap">
                      <div class="nw-inst-table">
                        <div class="nw-inst-head">
                          <span>Installment</span>
                          <span class="text-center">Due Date</span>
                          <span class="text-end">Amount</span>
                          <span class="text-end">Paid</span>
                          <span class="text-end">Balance</span>
                          <span class="text-center">Payment Mode</span>
                          <span class="text-center">Bill</span>
                          <span class="text-center">Status</span>
                        </div>

                        <ng-container *ngFor="let mandatoryRow of selectedStudentFeeView.mandatoryInstallmentRows; trackBy: trackByMandatoryInstallment">
                          <div class="nw-inst-row" [class.inst-row-paid]="mandatoryRow.balance + mandatoryRow.lateFee <= 0">
                            <span class="nw-inst-name">
                              <input
                                *ngIf="isCollectingMode && mandatoryRow.balance + mandatoryRow.lateFee > 0"
                                type="checkbox"
                                class="form-check-input me-2 afm-installment-check"
                                [checked]="isMandatoryCombinedInstallmentSelected(selectedStudentFeeView, mandatoryRow)"
                                (click)="$event.stopPropagation()"
                                (change)="toggleMandatoryCombinedInstallment(selectedStudentFeeView, mandatoryRow, $event)" />
                              <strong>{{ mandatoryRow.installmentName }}</strong>
                              <button
                                type="button"
                                class="btn btn-link btn-sm p-0 d-block text-start"
                                style="font-size:11px;text-decoration:none"
                                (click)="toggleMandatoryInstallmentDetails(mandatoryRow.installmentNumber)">
                                <i class="bi me-1" [ngClass]="expandedMandatoryInstallmentNumbers.has(mandatoryRow.installmentNumber) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                                {{ expandedMandatoryInstallmentNumbers.has(mandatoryRow.installmentNumber) ? 'Hide Details' : 'Details' }}
                                ({{ mandatoryRow.charges.length }} fees)
                              </button>
                            </span>
                            <span class="text-center nw-inst-date">{{ mandatoryRow.dueDate }}</span>
                            <span class="text-end fw-semibold">{{ mandatoryRow.amount | currency:'INR':'symbol':'1.0-2' }}</span>
                            <span class="text-end nw-val-paid fw-semibold">{{ mandatoryRow.paid | currency:'INR':'symbol':'1.0-2' }}</span>
                            <span class="text-end nw-val-due fw-semibold">
                              {{ mandatoryRow.balance + mandatoryRow.lateFee | currency:'INR':'symbol':'1.0-2' }}
                              <small *ngIf="mandatoryRow.lateFee > 0" class="d-block text-danger">Incl. Late Fee {{ mandatoryRow.lateFee | currency:'INR':'symbol':'1.0-2' }}</small>
                            </span>
                            <span class="text-center">{{ getMandatoryInstallmentPaymentMode(mandatoryRow) }}</span>
                            <span class="text-center">
                              <button type="button" *ngIf="hasMandatoryInstallmentBill(mandatoryRow)"
                                class="afm-bill-link-btn"
                                (click)="toggleMandatoryInstallmentDetails(mandatoryRow.installmentNumber)">
                                <i class="bi bi-receipt-cutoff me-1"></i>Details
                              </button>
                              <span *ngIf="!hasMandatoryInstallmentBill(mandatoryRow)">—</span>
                            </span>
                            <span class="text-center">
                              <span *ngIf="!isCollectingMode || mandatoryRow.balance + mandatoryRow.lateFee <= 0"
                                class="nw-inst-badge"
                                [ngClass]="mandatoryRow.balance + mandatoryRow.lateFee <= 0 ? 'badge-paid' : 'badge-pending'">
                                {{ mandatoryRow.balance + mandatoryRow.lateFee <= 0 ? 'Paid' : (mandatoryRow.paid > 0 ? 'Partial' : 'Pending') }}
                              </span>
                              <button
                                type="button"
                                *ngIf="isCollectingMode && mandatoryRow.balance + mandatoryRow.lateFee > 0"
                                class="afm-mini-pay-btn mt-1"
                                [disabled]="isNavigating"
                                (click)="collectMandatoryCombinedInstallment(selectedStudentFeeView, mandatoryRow, collectFeeModal)">
                                <i class="bi bi-credit-card me-1"></i>Pay
                              </button>
                            </span>
                          </div>

                          <div *ngIf="expandedMandatoryInstallmentNumbers.has(mandatoryRow.installmentNumber)"
                            class="px-3 py-2 border-bottom" style="background:#f8fbff">
                            <div class="small fw-semibold mb-2" style="color:#011044">
                              {{ mandatoryRow.installmentName }} — Fee Breakdown
                            </div>
                            <div *ngFor="let charge of mandatoryRow.charges; trackBy: trackByMandatoryCharge" class="mb-2">
                              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                <div class="fw-semibold small">
                                  <i class="bi bi-dot me-1"></i>{{ charge.feeName }}
                                  <span class="text-muted fw-normal ms-1">{{ charge.dueDate }}</span>
                                </div>
                                <div class="small">
                                  <span class="fw-semibold">{{ charge.amount | currency:'INR':'symbol':'1.0-2' }}</span>
                                  <span class="text-muted ms-2">Paid {{ charge.paid | currency:'INR':'symbol':'1.0-2' }}</span>
                                  <span class="text-danger ms-2">Due {{ charge.balance + charge.lateFee | currency:'INR':'symbol':'1.0-2' }}</span>
                                  <button type="button" class="afm-bill-link-btn ms-2"
                                    *ngIf="charge.installment && hasFeeBillForInstallment(charge.installment.installmentId!)"
                                    (click)="openInstallmentBill(charge.installment.installmentId!, feeBillModal)">Bill</button>
                                  <button type="button" class="afm-bill-link-btn ms-2"
                                    *ngIf="!charge.installment && hasFeeBillForOneTimeFee(charge.fee)"
                                    (click)="openOneTimeFeeBill(charge.fee, feeBillModal)">Bill</button>
                                </div>
                              </div>
                              <div *ngFor="let component of charge.components" class="small text-muted ms-4 d-flex justify-content-between">
                                <span>{{ component.componentName }}</span>
                                <span>{{ component.amount | currency:'INR':'symbol':'1.0-2' }}</span>
                              </div>
                              <div *ngIf="charge.components.length > 0 && charge.amount > getMandatoryDetailComponentsTotal(charge)"
                                class="small text-muted ms-4 d-flex justify-content-between">
                                <span>Remaining charge under {{ charge.feeName }}</span>
                                <span>{{ charge.amount - getMandatoryDetailComponentsTotal(charge) | currency:'INR':'symbol':'1.0-2' }}</span>
                              </div>
                            </div>
                            <div class="d-flex justify-content-between fw-semibold small border-top pt-2 mt-2" style="color:#011044">
                              <span>Total {{ mandatoryRow.installmentName }}</span>
                              <span>{{ mandatoryRow.amount | currency:'INR':'symbol':'1.0-2' }}</span>
                            </div>
                          </div>
                        </ng-container>

                        <div *ngIf="isCollectingMode && getSelectedMandatoryInstallmentCount(selectedStudentFeeView) > 0"
                          class="text-end px-3 py-2 border-bottom">
                          <button type="button" class="afm-mini-pay-btn"
                            [disabled]="isNavigating"
                            (click)="collectSelectedMandatoryInstallments(selectedStudentFeeView, collectFeeModal)">
                            <i class="bi bi-credit-card me-1"></i>Pay Selected
                            ({{ getSelectedMandatoryInstallmentCount(selectedStudentFeeView) }})
                          </button>
                        </div>
                        <div class="nw-subtotal-row">
                          <span>MANDATORY TOTAL</span><span></span>
                          <span class="text-end">{{ getMandatoryFeeTotal(selectedStudentFeeView) | currency:'INR':'symbol':'1.0-2' }}</span>
                          <span class="text-end nw-val-paid">{{ getMandatoryPaidTotal(selectedStudentFeeView) | currency:'INR':'symbol':'1.0-2' }}</span>
                          <span class="text-end nw-val-due">{{ getMandatoryPendingTotal(selectedStudentFeeView) | currency:'INR':'symbol':'1.0-2' }}</span>
                          <span></span><span></span><span></span>
                        </div>
                      </div>
                    </div>

                    <!-- Selected Payment Summary: Mandatory Fees (inline, same as original fee cards) -->
                    <ng-container *ngIf="isCollectingMode && getMandatorySelectedCollectionFee(selectedStudentFeeView) as mandatorySelectedFee">
                      <div class="afm-selected-payment-summary">

                        <h5>
                          <i class="bi bi-credit-card-2-front me-1"></i>
                          Selected for Payment
                        </h5>

                        <div class="afm-pay-input-row">
                          <label>Pay Amount:</label>
                          <input
                            type="number"
                            class="afm-pay-amount-input"
                            [class.afm-input-error]="customAmountExceedsMax"
                            min="1"
                            [max]="getFeeTotalPendingPayable(selectedStudentFeeView, mandatorySelectedFee)"
                            [ngModel]="getCustomCollectionAmountInputValue(selectedStudentFeeView, mandatorySelectedFee)"
                            (ngModelChange)="onCustomCollectionAmountChange($event, selectedStudentFeeView, mandatorySelectedFee)"
                            placeholder="Enter amount" />
                        </div>

                        <div *ngIf="customAmountExceedsMax" class="afm-amount-error">
                          <i class="bi bi-exclamation-triangle-fill me-1"></i>
                          Amount cannot exceed pending payable balance of
                          {{ getFeeTotalPendingPayable(selectedStudentFeeView, mandatorySelectedFee) | currency:'INR':'symbol':'1.0-2' }}
                        </div>

                        <div class="afm-summary-details">
                          <div class="afm-summary-row">
                            <span>Selected Installments:</span>
                            <span class="value">{{ getSelectedMandatoryInstallmentCount(selectedStudentFeeView) }}</span>
                          </div>

                          <div class="afm-summary-row">
                            <span>Installment Amount:</span>
                            <span class="value">
                              {{ getAllocatedInstallmentAmount(selectedStudentFeeView, mandatorySelectedFee) | currency:'INR':'symbol':'1.0-2' }}
                            </span>
                          </div>

                          <div class="afm-summary-row late-fee-row"
                            *ngIf="getAllocatedLateFeeAmount(selectedStudentFeeView, mandatorySelectedFee) > 0">
                            <span>Late Fees:</span>
                            <span class="value text-danger">
                              {{ getAllocatedLateFeeAmount(selectedStudentFeeView, mandatorySelectedFee) | currency:'INR':'symbol':'1.0-2' }}
                            </span>
                          </div>

                          <div class="afm-summary-row total-row">
                            <span><strong>Total Amount:</strong></span>
                            <span class="value">
                              <strong>
                                {{ getAllocatedTotalAmount(selectedStudentFeeView, mandatorySelectedFee) | currency:'INR':'symbol':'1.0-2' }}
                              </strong>
                            </span>
                          </div>
                        </div>

                        <div class="afm-allocation-preview"
                          *ngIf="getPaymentAllocationPlan(selectedStudentFeeView, mandatorySelectedFee).length > 0">
                          <div class="afm-allocation-title">Amount Allocation</div>

                          <div class="afm-allocation-row"
                            *ngFor="let allocation of getPaymentAllocationPlan(selectedStudentFeeView, mandatorySelectedFee); trackBy: trackByPaymentAllocationRow">
                            <span>{{ allocation.inst.installmentName }} - {{ allocation.fee.fName }}</span>
                            <strong>{{ allocation.totalAmount | currency:'INR':'symbol':'1.0-2' }}</strong>
                          </div>
                        </div>

                        <button
                          type="button"
                          class="afm-btn-proceed-pay"
                          [disabled]="isNavigating || customAmountExceedsMax || getAllocatedTotalAmount(selectedStudentFeeView, mandatorySelectedFee) <= 0"
                          (click)="openCollectFeeModal(selectedStudentFeeView, mandatorySelectedFee, collectFeeModal)">
                          <i class="bi bi-cash-coin me-1"></i>
                          <span *ngIf="!isNavigating">Collect Fees</span>
                          <span *ngIf="isNavigating">Processing...</span>
                        </button>

                      </div>
                    </ng-container>
                  </div>
                </div>
              </div>
            </div>

            <!-- Optional Fee Groups (unchanged card layout and details) -->
            <div class="nw-groups" *ngIf="selectedStudentFeeView.feeGroups.length > 0 && getEffectiveFeeDisplayTab(selectedStudentFeeView) === 'other'">
              <div
                *ngFor="let group of selectedStudentFeeView.feeGroups; trackBy: trackByFeeType"
                class="nw-group">

                <div class="nw-group-header">
                  <span class="nw-group-title">
                    <i class="bi bi-folder2-open me-2"></i>
                    {{ feeTypeLabels[group.feeType] || group.feeType }}
                  </span>

                  <span class="nw-group-total">
                    {{ getGroupTotal(group) | currency:'INR':'symbol':'1.0-2' }}
                  </span>
                </div>

                <div class="nw-fee-cards">
                  <div
                    *ngFor="let fee of group.fees; trackBy: trackByFeeId"
                    class="nw-fee-card"
                    [class.nw-card-paid]="isFeeFullyPaid(selectedStudentFeeView, fee)"
                    [class.nw-card-overdue]="hasFeeOverdue(selectedStudentFeeView, fee) && !isFeeFullyPaid(selectedStudentFeeView, fee)">

                    <!-- Card Head -->
                    <div class="nw-card-head">
                      <div
                        class="nw-card-icon"
                        [ngClass]="getFeeIconClass(selectedStudentFeeView, fee)">
                        <i class="bi" [ngClass]="getFeeIcon(selectedStudentFeeView, fee)"></i>
                      </div>

                      <div class="nw-card-title-block">
                        <div class="nw-card-name">{{ fee.fName }}</div>

                        <div class="nw-card-meta">
                          <span class="nw-card-amt">
                            {{ fee.amount | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span
                            class="nw-status-chip"
                            [ngClass]="getFeeStatusChipClass(selectedStudentFeeView, fee)">
                            <i
                              class="bi me-1"
                              [ngClass]="getFeeStatusIcon(selectedStudentFeeView, fee)">
                            </i>
                            {{ getFeeStatus(selectedStudentFeeView, fee) }}
                          </span>
                        </div>
                      </div>

                      <!-- Ring -->
                      <div class="nw-ring-wrap">
                        <svg viewBox="0 0 44 44" class="nw-ring">
                          <circle cx="22" cy="22" r="18" class="nw-ring-track"></circle>
                          <circle
                            cx="22"
                            cy="22"
                            r="18"
                            class="nw-ring-fill"
                            [style.stroke-dashoffset]="getRingOffset(selectedStudentFeeView, fee)"
                            [ngClass]="
                              isFeeFullyPaid(selectedStudentFeeView, fee)
                                ? 'ring-paid'
                                : (hasFeeOverdue(selectedStudentFeeView, fee) ? 'ring-overdue' : 'ring-partial')
                            ">
                          </circle>
                        </svg>
                        <span class="nw-ring-pct">
                          {{ getFeeProgressPercent(selectedStudentFeeView, fee) }}%
                        </span>
                      </div>
                    </div>

                    <!-- Card Strip -->
                    <div class="nw-card-strip">
                      <div class="nw-strip-item">
                        <span class="nw-strip-label">Paid</span>
                        <span class="nw-strip-val nw-val-paid">
                          {{ getFeePaid(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                        </span>
                      </div>

                      <div class="nw-strip-divider"></div>

                      <div class="nw-strip-item">
                        <span class="nw-strip-label">Pending</span>
                        <span class="nw-strip-val nw-val-due">
                          {{ getFeePending(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                        </span>
                      </div>

                      <ng-container *ngIf="getFeeLateFees(selectedStudentFeeView, fee) > 0">
                        <div class="nw-strip-divider"></div>

                        <div class="nw-strip-item">
                          <span class="nw-strip-label">Late Fee</span>
                          <span class="nw-strip-val nw-val-late">
                            {{ getFeeLateFees(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                          </span>
                        </div>
                      </ng-container>
                    </div>

                    <!-- Mini Bar -->
                    <div class="nw-mini-bar">
                      <div
                        class="nw-mini-fill"
                        [style.width.%]="getFeeProgressPercent(selectedStudentFeeView, fee)"
                        [ngClass]="
                          isFeeFullyPaid(selectedStudentFeeView, fee)
                            ? 'mf-paid'
                            : (hasFeeOverdue(selectedStudentFeeView, fee) ? 'mf-overdue' : 'mf-partial')
                        ">
                      </div>
                    </div>

                    <!-- Installments Toggle -->
                    <div
                      class="nw-inst-wrap"
                      *ngIf="fee.installments && fee.installments.length > 0">

                      <button
                        class="nw-inst-toggle"
                        (click)="toggleFeeExpand(selectedStudentFeeView, fee.feeStructureId)">
                        <i
                          class="bi me-1"
                          [ngClass]="
                            selectedStudentFeeView.expandedFeeIds.has(fee.feeStructureId)
                              ? 'bi-chevron-up'
                              : 'bi-chevron-down'
                          ">
                        </i>
                        {{ selectedStudentFeeView.expandedFeeIds.has(fee.feeStructureId) ? 'Hide' : 'View' }}
                        Installments ({{ fee.installments.length }})
                      </button>

                      <div
                        class="nw-inst-table"
                        *ngIf="selectedStudentFeeView.expandedFeeIds.has(fee.feeStructureId)">

                        <div class="nw-inst-head">
                          <span>Installment</span>
                          <span class="text-center">Due Date</span>
                          <span class="text-end">Amount</span>
                          <span class="text-end">Paid</span>
                          <span class="text-end">Balance</span>
                          <span class="text-center">Payment Mode</span>
                          <span class="text-center">Bill</span>
                          <span class="text-center">Status</span>
                        </div>

                        <ng-container *ngFor="let inst of fee.installments; let i = index; trackBy: trackByInstId">
                        <div
                          class="nw-inst-row"
                          [ngClass]="getInstRowClass(selectedStudentFeeView, inst)"
                          [style.animation-delay]="(i * 0.04) + 's'">

                          <span class="nw-inst-name">
                            <input
                              *ngIf="isCollectingMode && canCollectInstallment(selectedStudentFeeView, inst)"
                              type="checkbox"
                              class="form-check-input me-2 afm-installment-check"
                              [checked]="isInstallmentSelected(inst.installmentId!)"
                              (click)="$event.stopPropagation()"
                              (change)="toggleCollectionInstallment(selectedStudentFeeView, inst, $event)" />

                            <span>{{ inst.installmentName }}</span>
                            <button
                              type="button"
                              (click)="toggleInstallmentDetails(inst.installmentId!)"
                              [attr.aria-expanded]="expandedInstallmentDetailIds.has(inst.installmentId!)"
                              style="display:block; padding:2px 0 0; border:0; background:transparent; color:#24488e; font-size:11px; cursor:pointer; text-align:left;">
                              <i class="bi me-1" [ngClass]="expandedInstallmentDetailIds.has(inst.installmentId!) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                              Details
                            </button>
                          </span>

                          <span
                            class="text-center nw-inst-date"
                            [class.nw-overdue-date]="
                              isOverdue(getInstallmentDueDateValue(inst, fee, i, fee.installments?.length || 0)) &&
                              !isInstallmentPaid(selectedStudentFeeView, inst.installmentId!)
                            ">
                            <i class="bi bi-calendar3 me-1" style="font-size:10px"></i>
                            {{ getInstallmentDueDateText(inst, fee, i, fee.installments?.length || 0) }}
                          </span>

                          <span class="text-end fw-semibold">
                            {{ inst.amount | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span class="text-end nw-val-paid fw-semibold">
                            {{ getInstallmentPaidAmount(selectedStudentFeeView, inst.installmentId!) | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span
                            class="text-end fw-semibold"
                            [class.nw-val-due]="getInstallmentRemainingAmount(selectedStudentFeeView, inst.installmentId!) > 0">
                            {{ getInstallmentRemainingAmount(selectedStudentFeeView, inst.installmentId!) | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span class="text-center">
                            <span
                              *ngIf="isInstallmentPaid(selectedStudentFeeView, inst.installmentId!)"
                              class="afm-payment-mode-chip"
                              [ngClass]="getInstallmentPaymentModeClass(inst.installmentId!)">
                              {{ getInstallmentPaymentMode(inst.installmentId!) }}
                            </span>
                            <span
                              *ngIf="!isInstallmentPaid(selectedStudentFeeView, inst.installmentId!)"
                              class="afm-bill-empty">
                              —
                            </span>
                          </span>

                          <span class="text-center">
                            <button
                              type="button"
                              *ngIf="hasFeeBillForInstallment(inst.installmentId!)"
                              class="afm-bill-link-btn"
                              (click)="openInstallmentBill(inst.installmentId!, feeBillModal)">
                              <i class="bi bi-receipt-cutoff me-1"></i>
                              Bill
</button>
                            <span
                              *ngIf="!hasFeeBillForInstallment(inst.installmentId!)"
                              class="afm-bill-empty">
                              —
                            </span>
                          </span>

                          <span class="text-center">
                            <span
                              *ngIf="!isCollectingMode || !canCollectInstallment(selectedStudentFeeView, inst)"
                              class="nw-inst-badge"
                              [ngClass]="getInstBadgeClass(selectedStudentFeeView, inst)">
                              <i
                                class="bi me-1"
                                [ngClass]="getInstBadgeIcon(selectedStudentFeeView, inst)">
                              </i>
                              {{ getInstStatusLabel(selectedStudentFeeView, inst) }}
                            </span>

                            <button
                              type="button"
                              *ngIf="isCollectingMode && canCollectInstallment(selectedStudentFeeView, inst)"
                              class="afm-mini-pay-btn mt-1"
                              (click)="collectSingleInstallment(selectedStudentFeeView, inst, collectFeeModal)">
                              <i class="bi bi-credit-card me-1"></i>
                              Pay
                            </button>
                          </span>
                        </div>

                        <!-- Installment Fee Breakup (view only) -->
                        <div
                          *ngIf="expandedInstallmentDetailIds.has(inst.installmentId!)"
                          style="margin:0 14px 10px 32px; padding:12px 16px; border:1px solid #e0e8f4; background:#f8faff; border-radius:7px; font-size:12px;">
                          <div style="font-weight:700; color:#011044; margin-bottom:8px;">
                            {{ inst.installmentName }} — Fee Details
                          </div>

                          <ng-container *ngIf="getInstallmentDetailComponents(inst).length > 0; else installmentFeeNameOnly">
                            <div
                              *ngFor="let component of getInstallmentDetailComponents(inst)"
                              style="display:flex; justify-content:space-between; gap:12px; padding:4px 0; border-bottom:1px solid #edf0f5;">
                              <span>{{ component.componentName }}</span>
                              <span>{{ component.amount | currency:'INR':'symbol':'1.0-2' }}</span>
                            </div>
                            <div
                              *ngIf="getInstallmentDetailUnitemizedAmount(inst) > 0"
                              style="display:flex; justify-content:space-between; gap:12px; padding:4px 0;">
                              <span>Other amount (not itemized)</span>
                              <span>{{ getInstallmentDetailUnitemizedAmount(inst) | currency:'INR':'symbol':'1.0-2' }}</span>
                            </div>
                            <div
                              *ngIf="getInstallmentDetailComponentTotal(inst) > (inst.amount || 0)"
                              class="text-danger" style="padding-top:6px;">
                              Configured charge components exceed the installment amount. Please review the fee setup.
                            </div>
                          </ng-container>

                          <ng-template #installmentFeeNameOnly>
                            <div style="display:flex; justify-content:space-between; gap:12px; padding:4px 0;">
                              <span>{{ fee.fName }}</span>
                              <span>{{ inst.amount | currency:'INR':'symbol':'1.0-2' }}</span>
                            </div>
                          </ng-template>

                          <div style="display:flex; justify-content:space-between; gap:12px; border-top:1px solid #d7dfec; margin-top:6px; padding-top:8px; font-weight:700; color:#011044;">
                            <span>Installment Total</span>
                            <span>{{ inst.amount | currency:'INR':'symbol':'1.0-2' }}</span>
                          </div>
                        </div>
                        </ng-container>

                        <!-- Late Fee Row -->
                        <div
                          class="nw-inst-row nw-late-row"
                          *ngIf="getFeeLateFees(selectedStudentFeeView, fee) > 0">

                          <span class="nw-inst-name nw-val-late">
                            <i class="bi bi-exclamation-triangle-fill me-1"></i>
                            Late Fee
                          </span>

                          <span class="text-center">—</span>
                          <span></span>
                          <span></span>

                          <span class="text-end nw-val-due fw-bold">
                            {{ getFeeLateFees(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span class="text-center">—</span>

                          <span class="text-center">—</span>

                          <span class="text-center">
                            <span class="nw-inst-badge badge-overdue">
                              <i class="bi bi-exclamation-triangle-fill me-1"></i>
                              Overdue
                            </span>
                          </span>
                        </div>

                        <!-- Subtotal -->
                        <div class="nw-subtotal-row">
                          <span>Subtotal</span>
                          <span></span>

                          <span class="text-end">
                            {{ fee.amount | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span class="text-end nw-val-paid">
                            {{ getFeePaid(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span class="text-end nw-val-due">
                            {{ getFeePending(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                          </span>

                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>

                    <!-- Selected Payment Summary -->
                    <div
                      *ngIf="isCollectingMode && getFeeSelectedCollectionCount(selectedStudentFeeView, fee) > 0"
                      class="afm-selected-payment-summary">

                      <h5>
                        <i class="bi bi-credit-card-2-front me-1"></i>
                        Selected for Payment
                      </h5>

                      <div class="afm-pay-input-row">
                        <label>Pay Amount:</label>
                        <input
                          type="number"
                          class="afm-pay-amount-input"
                          [class.afm-input-error]="customAmountExceedsMax"
                          min="1"
                          [max]="getFeeTotalPendingPayable(selectedStudentFeeView, fee)"
                          [ngModel]="getCustomCollectionAmountInputValue(selectedStudentFeeView, fee)"
                          (ngModelChange)="onCustomCollectionAmountChange($event, selectedStudentFeeView, fee)"
                          placeholder="Enter amount" />
                      </div>

                      <div *ngIf="customAmountExceedsMax" class="afm-amount-error">
                        <i class="bi bi-exclamation-triangle-fill me-1"></i>
                        Amount cannot exceed pending payable balance of {{ getFeeTotalPendingPayable(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                      </div>

                      <div class="afm-summary-details">
                        <div class="afm-summary-row">
                          <span>Selected Installments:</span>
                          <span class="value">
                            {{ getFeeSelectedCollectionCount(selectedStudentFeeView, fee) }}
                          </span>
                        </div>

                        <div class="afm-summary-row">
                          <span>Installment Amount:</span>
                          <span class="value">
                            {{ getAllocatedInstallmentAmount(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                          </span>
                        </div>

                        <div
                          *ngIf="getAllocatedLateFeeAmount(selectedStudentFeeView, fee) > 0"
                          class="afm-summary-row late-fee-row">
                          <span>Late Fees:</span>
                          <span class="value text-danger">
                            {{ getAllocatedLateFeeAmount(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                          </span>
                        </div>

                        <div class="afm-summary-row total-row">
                          <span><strong>Total Amount:</strong></span>
                          <span class="value">
                            <strong>
                              {{ getAllocatedTotalAmount(selectedStudentFeeView, fee) | currency:'INR':'symbol':'1.0-2' }}
                            </strong>
                          </span>
                        </div>
                      </div>

                      <div
                        class="afm-allocation-preview"
                        *ngIf="getPaymentAllocationPlan(selectedStudentFeeView, fee).length > 0">
                        <div class="afm-allocation-title">Amount Allocation</div>

                        <div
                          class="afm-allocation-row"
                          *ngFor="let row of getPaymentAllocationPlan(selectedStudentFeeView, fee); trackBy: trackByPaymentAllocationRow">
                          <span>{{ row.inst.installmentName }}</span>
                          <strong>
                            {{ row.totalAmount | currency:'INR':'symbol':'1.0-2' }}
                          </strong>
                        </div>
                      </div>

                      <button
                        type="button"
                        class="afm-btn-proceed-pay"
                        [disabled]="isNavigating || customAmountExceedsMax || getAllocatedTotalAmount(selectedStudentFeeView, fee) <= 0"
                        (click)="openCollectFeeModal(selectedStudentFeeView, fee, collectFeeModal)">
                        <i class="bi bi-cash-coin me-1"></i>
                        <span *ngIf="!isNavigating">Collect Fees</span>
                        <span *ngIf="isNavigating">Processing...</span>
                      </button>
                    </div>

                    <!-- One-time fee -->
                    <!-- One-time fee -->
<div
  class="nw-onetime d-flex flex-wrap align-items-center gap-2"
  *ngIf="!fee.installments || fee.installments.length === 0">

  <span>
    <i class="bi bi-info-circle me-1"></i>
    One-time fee.
  </span>

  <!-- Paid Text -->
  <span
    *ngIf="isFeeFullyPaid(selectedStudentFeeView!, fee)"
    class="nw-val-paid fw-semibold">
    <i class="bi bi-check-circle-fill me-1"></i>
    Payment received.
  </span>

  <!-- Payment Mode for one-time fee -->
  <span
    *ngIf="isFeeFullyPaid(selectedStudentFeeView!, fee)"
    class="afm-payment-mode-chip"
    [ngClass]="getOneTimeFeePaymentModeClass(selectedStudentFeeView!, fee)">
    {{ getOneTimeFeePaymentMode(selectedStudentFeeView!, fee) }}
  </span>

  <!-- Bill button for one-time fee -->
  <button
    type="button"
    *ngIf="hasFeeBillForOneTimeFee(fee)"
    class="afm-bill-link-btn"
    (click)="openOneTimeFeeBill(fee, feeBillModal)">
    <i class="bi bi-receipt-cutoff me-1"></i>
    Bill
  </button>

  <!-- Select checkbox for collection mode -->
  <label
    *ngIf="isCollectingMode && canCollectOneTimeFee(selectedStudentFeeView!, fee)"
    class="d-inline-flex align-items-center gap-2 ms-2 mb-0"
    style="cursor: pointer;">
    <input
      type="checkbox"
      [checked]="isOneTimeFeeSelected(fee)"
      (change)="toggleCollectionOneTimeFee(selectedStudentFeeView!, fee, $event)" />
    <span>Select for payment</span>
  </label>

  <!-- Pay button when not in collection mode -->
  <button
    type="button"
    *ngIf="!isCollectingMode && canCollectOneTimeFee(selectedStudentFeeView!, fee)"
    class="btn btn-sm btn-success ms-2"
    (click)="collectOneTimeFee(selectedStudentFeeView!, fee, collectFeeModal)">
    <i class="bi bi-credit-card me-1"></i>
    Pay
  </button>
</div>

                  </div>
                </div>
              </div>
            </div>

            <!-- Footer Totals -->
            <div class="nw-footer-totals">
              <div class="nw-ft-row">
                <span>Total Fee Amount</span>
                <span>
                  {{ selectedStudentFeeView.grandTotal | currency:'INR':'symbol':'1.0-2' }}
                </span>
              </div>

              <div class="nw-ft-row nw-ft-paid">
                <span>Amount Paid</span>
                <span>
                  {{ selectedStudentFeeView.grandPaid | currency:'INR':'symbol':'1.0-2' }}
                </span>
              </div>

              <div class="nw-ft-row nw-ft-late" *ngIf="selectedStudentFeeView.grandLateFees > 0">
                <span>Late Fees</span>
                <span>
                  {{ selectedStudentFeeView.grandLateFees | currency:'INR':'symbol':'1.0-2' }}
                </span>
              </div>

              <div class="nw-ft-divider"></div>

              <div class="nw-ft-row nw-ft-total">
                <span>Net Outstanding</span>
                <span
                  [class.nw-val-due]="selectedStudentFeeView.grandDue > 0"
                  [class.nw-val-paid]="selectedStudentFeeView.grandDue === 0">
                  {{ selectedStudentFeeView.grandDue | currency:'INR':'symbol':'1.0-2' }}
                </span>
              </div>
            </div>

          </ng-container>
        </ng-container>

      </ng-container>
    </ng-container>
    </ng-container> <!-- end activeTab === 'students' -->

  </ng-container>
</div>

<!-- ═══════════════════════════════════════════════════
     COLLECT FEES PAYMENT METHOD MODAL
════════════════════════════════════════════════════ -->
<ng-template #collectFeeModal let-modal>
  <div class="modal-header afm-modal-header">
    <h5 class="modal-title">
      <i class="bi bi-cash-coin me-2"></i>
      Collect Fees
    </h5>
    <button
      type="button"
      class="btn-close btn-close-white"
      (click)="modal.dismiss()"
      [disabled]="isNavigating || isSendingPaymentLink">
    </button>
  </div>

  <div class="modal-body afm-modal-body afm-collect-modal-body">
    <div class="afm-collect-modal-summary">
      <div>
        <div class="afm-collect-modal-label">Student</div>
        <div class="afm-collect-modal-value">{{ getCollectionStudentNameForModal() }}</div>
      </div>

      <div>
        <div class="afm-collect-modal-label">Selected Amount</div>
        <div class="afm-collect-modal-amount">
          {{ getCollectionAmountForModal() | currency:'INR':'symbol':'1.0-2' }}
        </div>
      </div>
    </div>

    <div class="afm-payment-method-title">
      Choose Payment Method
    </div>

    <div class="afm-payment-method-grid">
      <button
        type="button"
        class="afm-payment-method-card"
        [class.afm-payment-method-active]="selectedCollectionMethod === 'cash'"
        (click)="selectCollectionMethod('cash')">
        <span class="afm-method-icon afm-method-cash">
          <i class="bi bi-cash-stack"></i>
        </span>
        <span class="afm-method-content">
          <strong>Cash Collection</strong>
          <small>Collect cash at counter and update payment on this same screen.</small>
        </span>
        <i class="bi bi-check-circle-fill afm-method-check" *ngIf="selectedCollectionMethod === 'cash'"></i>
      </button>

      <button
        type="button"
        class="afm-payment-method-card"
        [class.afm-payment-method-active]="selectedCollectionMethod === 'qr'"
        (click)="selectCollectionMethod('qr')">
        <span class="afm-method-icon afm-method-qr">
          <i class="bi bi-qr-code"></i>
        </span>
        <span class="afm-method-content">
          <strong>Pay For QR Code</strong>
          <small>Collect QR/online payment and update payment on this same screen.</small>
        </span>
        <i class="bi bi-check-circle-fill afm-method-check" *ngIf="selectedCollectionMethod === 'qr'"></i>
      </button>

      <button
        type="button"
        class="afm-payment-method-card"
        [class.afm-payment-method-active]="selectedCollectionMethod === 'link'"
        (click)="selectCollectionMethod('link')">
        <span class="afm-method-icon afm-method-link">
          <i class="bi bi-link-45deg"></i>
        </span>
        <span class="afm-method-content">
          <strong>Send Payment Link</strong>
          <small>Create invoice and send payment link to parent by configured notification API.</small>
        </span>
        <i class="bi bi-check-circle-fill afm-method-check" *ngIf="selectedCollectionMethod === 'link'"></i>
      </button>
    </div>

    <div class="afm-method-info" [ngSwitch]="selectedCollectionMethod">
      <div *ngSwitchCase="'cash'">
        <i class="bi bi-info-circle-fill me-1"></i>
        Cash mode will create pending invoice/detail records and open the payment page. Complete the cash payment there; this screen will refresh and generate the bill.
      </div>

      <div *ngSwitchCase="'qr'">
        <i class="bi bi-qr-code me-1"></i>
        QR mode will generate a payment QR code. The fee status and bill will update only after the QR payment is completed successfully.
      </div>

      <div *ngSwitchCase="'link'">
        <i class="bi bi-send-fill me-1"></i>
        Payment link mode will create the same pending invoice and send the generated payment link to the parent. Status will update after payment success.
      </div>
    </div>


<div
  class="afm-qr-preview"
  *ngIf="selectedCollectionMethod === 'qr' && isQrCodeReady && generatedCollectionLink">

  <div class="afm-qr-box">
    <qrcode
      [qrdata]="generatedCollectionLink"
      [width]="240"
      [errorCorrectionLevel]="'M'">
    </qrcode>
  </div>

  <div class="afm-qr-content">
    <div class="afm-qr-title">
      <i class="bi bi-qr-code me-1"></i>
      QR Code Generated
    </div>

    <div class="afm-qr-sub">
      Parent can scan this QR code and complete payment.
      After payment, click refresh to reload fee status.
    </div>

    <div class="afm-qr-actions">
      <button
        type="button"
        class="afm-qr-action-btn"
        (click)="copyGeneratedPaymentLink()">
        <i class="bi bi-copy me-1"></i>
        Copy Link
      </button>

      <button
        type="button"
        class="afm-qr-action-btn afm-qr-refresh-btn"
        (click)="refreshCollectionStatusFromModal()">
        <i class="bi bi-arrow-clockwise me-1"></i>
        Refresh Status
      </button>
    </div>
  </div>
</div>


</div> <!-- Close modal body -->

<div class="modal-footer afm-modal-footer afm-collect-modal-footer">
    <button
      type="button"
      class="universal-cancel-btn"
      (click)="modal.dismiss()"
      [disabled]="isNavigating || isSendingPaymentLink">
      Cancel
    </button>

    <button
      type="button"
      class="universal-submit-btn afm-confirm-collect-btn"
      [disabled]="isNavigating || isSendingPaymentLink || getCollectionAmountForModal() <= 0"
      (click)="confirmCollectFeesFromModal()">
      <span *ngIf="!isNavigating && !isSendingPaymentLink">
        <i class="bi bi-check2-circle me-1"></i>
        {{ getCollectionConfirmButtonText() }}
      </span>

      <span *ngIf="isNavigating || isSendingPaymentLink" class="afm-btn-sending">
        <div class="afm-btn-spinner"></div>
        Processing…
      </span>
    </button>
  </div>
</ng-template>


<!-- ============================================================
     SCHOOL EDIT PAID AMOUNT MODAL
     ============================================================ -->

<ng-template #editPaidAmountModal let-modal>

  <div class="modal-header afm-modal-header">

    <h5 class="modal-title">
      <i class="bi bi-pencil-square me-2"></i>
      Edit Paid Amount
    </h5>

    <button
      type="button"
      class="btn-close btn-close-white"
      (click)="modal.dismiss()"
      [disabled]="isPaidEditSaving"
    ></button>

  </div>


  <div class="modal-body afm-modal-body">

    <div class="afm-paid-edit-student" *ngIf="paidEditStudentView">

      <div>
        <small>Student</small>

        <strong>
          {{ paidEditStudentView.student.firstName }}
          {{ paidEditStudentView.student.lastName }}
        </strong>
      </div>

      <div>
        <small>Current Total Paid</small>

        <strong>
          {{
            paidEditStudentView.grandPaid |
            currency:'INR':'symbol':'1.2-2'
          }}
        </strong>
      </div>

    </div>


    <div
      class="afm-loader-row"
      *ngIf="isPaidEditLoading"
    >
      <div class="afm-spinner"></div>
      <span>Loading paid transactions...</span>
    </div>


    <div
      class="alert alert-danger"
      *ngIf="paidEditError"
    >
      {{ paidEditError }}
    </div>


    <div
      class="afm-paid-edit-table-wrap"
      *ngIf="!isPaidEditLoading"
    >

      <table class="table afm-paid-edit-table">

        <thead>
          <tr>
            <th>Sr.</th>
            <th>Fee / Installment</th>
            <th>Invoice ID</th>
            <th>Detail ID</th>
            <th>Payment Date</th>
            <th>Current Paid</th>
            <th>New Paid Amount</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>

          <tr
            *ngFor="let row of paidEditRows; let i = index"
          >

            <td>{{ i + 1 }}</td>

            <td>
              <strong>{{ row.feeName }}</strong>

              <small class="d-block text-muted">
                {{ row.installmentName }}
              </small>
            </td>

            <td>{{ row.invoiceId }}</td>

            <td>{{ row.detailId }}</td>

            <td>
              {{ row.paymentDate | date:'dd MMM yyyy, hh:mm a' }}
            </td>

            <td>
              {{
                row.originalAmount |
                currency:'INR':'symbol':'1.2-2'
              }}
            </td>

            <td>

              <input
                type="number"
                class="form-control form-control-sm"
                [(ngModel)]="row.newAmount"
                [ngModelOptions]="{standalone: true}"
                min="0.01"
                step="0.01"
                [disabled]="isPaidEditSaving"
              />

            </td>

            <td>

              <button
                type="button"
                class="afm-paid-edit-save"
                (click)="saveEditedSchoolPayment(row)"
                [disabled]="
                  isPaidEditSaving ||
                  row.newAmount === null ||
                  row.newAmount === undefined ||
                  +row.newAmount === +row.originalAmount
                "
              >
                <i class="bi bi-check2"></i>
                Save
              </button>

            </td>

          </tr>


          <tr *ngIf="paidEditRows.length === 0">

            <td colspan="8" class="text-center py-4 text-muted">
              No paid transactions found for this student.
            </td>

          </tr>

        </tbody>

      </table>

    </div>


    <div class="afm-paid-edit-total">

      <div>
        <span>Current Listed Payments</span>

        <strong>
          {{
            paidEditCurrentTotal |
            currency:'INR':'symbol':'1.2-2'
          }}
        </strong>
      </div>

      <div>
        <span>Proposed Listed Payments</span>

        <strong>
          {{
            paidEditProposedTotal |
            currency:'INR':'symbol':'1.2-2'
          }}
        </strong>
      </div>

    </div>


    <div class="afm-paid-edit-note">
      <i class="bi bi-info-circle me-1"></i>
      Amount changes are previews until the payment correction
      API saves and reconciles the transaction and receipt.
    </div>

  </div>


  <div class="modal-footer afm-modal-footer">

    <button
      type="button"
      class="afm-btn-cancel"
      (click)="modal.close()"
      [disabled]="isPaidEditSaving"
    >
      Close
    </button>

  </div>

</ng-template>


<!-- ═══════════════════════════════════════════════════
     PAYMENT BILL MODAL
════════════════════════════════════════════════════ -->
<ng-template #feeBillModal let-modal>
  <div class="modal-header afm-modal-header afm-bill-modal-header">
    <h5 class="modal-title">
      <i class="bi bi-receipt-cutoff me-2"></i>
      Payment Bill
    </h5>
    <button
      type="button"
      class="btn-close btn-close-white"
      (click)="modal.dismiss()"
      [disabled]="isSendingFeeBill">
    </button>
  </div>

  <div class="modal-body afm-modal-body afm-bill-modal-body" *ngIf="lastGeneratedBill">
    <div class="afm-bill-card" id="feeBillPrintArea">
      <div class="afm-bill-top">
        <div>
          <div class="afm-bill-school">{{ lastGeneratedBill.schoolName }}</div>
          <div class="afm-bill-sub">Fee Payment Receipt</div>
          <div class="afm-bill-receipt">Receipt No: {{ lastGeneratedBill.receiptNo }}</div>
        </div>

        <span
          class="afm-bill-status"
          [class.afm-bill-status-partial]="lastGeneratedBill.remainingAmount > 0">
          <i
            class="bi me-1"
            [ngClass]="lastGeneratedBill.remainingAmount > 0 ? 'bi-clock-history' : 'bi-check-circle-fill'">
          </i>
          {{ lastGeneratedBill.status }}
        </span>
      </div>

      <div class="afm-bill-grid">
        <div class="afm-bill-item">
          <label>Student</label>
          <span>{{ lastGeneratedBill.studentName }}</span>
        </div>

        <div class="afm-bill-item">
          <label>Class / Section</label>
          <span>{{ lastGeneratedBill.className }} / {{ lastGeneratedBill.sectionName }}</span>
        </div>

        <div class="afm-bill-item">
          <label>Fee</label>
          <span>{{ lastGeneratedBill.feeName }}</span>
        </div>

        <div class="afm-bill-item">
          <label>Installment</label>
          <span>{{ lastGeneratedBill.installmentName }}</span>
        </div>

        <div class="afm-bill-item">
          <label>Payment Method</label>
          <span>{{ lastGeneratedBill.paymentMethod }}</span>
        </div>

        <div class="afm-bill-item">
          <label>Paid Date</label>
          <span>{{ getBillDateText(lastGeneratedBill) }}</span>
        </div>
      </div>

      <div class="afm-bill-amount-grid">
        <div class="afm-bill-amount-box">
          <span>Installment Amount</span>
          <strong>{{ lastGeneratedBill.installmentAmount | currency:'INR':'symbol':'1.0-2' }}</strong>
        </div>

        <div class="afm-bill-amount-box afm-bill-paid-box">
          <span>Paid Amount</span>
          <strong>{{ (lastGeneratedBill.paidAmount || lastGeneratedBill.amount) | currency:'INR':'symbol':'1.0-2' }}</strong>
        </div>

        <div class="afm-bill-amount-box afm-bill-balance-box">
          <span>Remaining Balance</span>
          <strong>{{ lastGeneratedBill.remainingAmount | currency:'INR':'symbol':'1.0-2' }}</strong>
        </div>
      </div>

      <div *ngIf="lastGeneratedBill.feeBreakdown && lastGeneratedBill.feeBreakdown.length > 0" class="afm-bill-payment-history">
        <div class="afm-bill-history-title">Mandatory Fee Breakdown</div>
        <div class="d-flex justify-content-between border-bottom small py-2" *ngFor="let item of lastGeneratedBill.feeBreakdown">
          <span>{{ item.feeName }} — {{ item.installmentName }}</span>
          <strong>{{ item.amount | currency:'INR':'symbol':'1.0-2' }}</strong>
        </div>
      </div>

      <div
  class="afm-bill-payment-history"
  *ngIf="lastGeneratedBill.paymentHistory && lastGeneratedBill.paymentHistory.length > 0">

  <div class="afm-bill-history-title">
    <i class="bi bi-clock-history me-1"></i>
    Payment History
  </div>

  <div class="afm-bill-history-table">
    <div class="afm-bill-history-row afm-bill-history-head">
      <span>#</span>
      <span>Date & Time</span>
      <span>Payment Mode</span>
      <span class="text-end">Paid Amount</span>
    </div>

    <div
      class="afm-bill-history-row"
      *ngFor="let row of lastGeneratedBill.paymentHistory; let i = index">
      <span>{{ i + 1 }}</span>
      <span>{{ getPaymentHistoryDateText(row) }}</span>
      <span>{{ row.paymentMethod || '—' }}</span>
      <span class="text-end afm-bill-history-paid">
        {{ row.amount | currency:'INR':'symbol':'1.0-2' }}
      </span>
    </div>
  </div>
</div>

      <div class="afm-bill-footer-note">
        Generated by {{ lastGeneratedBill.collectedBy }}. This is a system generated receipt for school fee collection.
      </div>
    </div>
  </div>

  <div class="modal-footer afm-modal-footer afm-bill-modal-footer">
    <button
      type="button"
      class="universal-cancel-btn"
      (click)="modal.dismiss()"
      [disabled]="isSendingFeeBill">
      Close
    </button>

    <button
      type="button"
      class="afm-bill-secondary-btn"
      (click)="printFeeBill()"
      [disabled]="isSendingFeeBill || !lastGeneratedBill">
      <i class="bi bi-printer me-1"></i>
      Print
    </button>

    <button
      type="button"
      class="afm-bill-secondary-btn"
      (click)="downloadFeeBill()"
      [disabled]="isSendingFeeBill || !lastGeneratedBill">
      <i class="bi bi-download me-1"></i>
      Download
    </button>

    <button
      type="button"
      class="universal-submit-btn afm-bill-send-btn"
      (click)="sendFeeBillToParent()"
      [disabled]="isSendingFeeBill || !lastGeneratedBill">
      <span *ngIf="!isSendingFeeBill">
        <i class="bi bi-send me-1"></i>
        Send Bill
      </span>
      <span *ngIf="isSendingFeeBill" class="afm-btn-sending">
        <div class="afm-btn-spinner"></div>
        Sending…
      </span>
    </button>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════════════
     SINGLE NOTIFY MODAL
════════════════════════════════════════════════════ -->
<ng-template #singleNotifyModal let-modal>
  <div class="modal-header afm-modal-header">
    <h5 class="modal-title">
      <i class="bi bi-send-fill me-2"></i>
      Send Fee Reminder
    </h5>
    <button
      type="button"
      class="btn-close btn-close-white"
      (click)="modal.dismiss()">
    </button>
  </div>

  <div class="modal-body afm-modal-body">

    <!-- Recipient Info -->
    <div class="afm-notify-recipient" *ngIf="notifySingleStudent">
      <div class="afm-avatar-sm afm-avatar-solid" style="background-color: #5c5cbf;">
        {{ getStudentInitials(notifySingleStudent) }}
      </div>

      <div class="afm-recipient-info">
        <div class="afm-stu-name">
          {{ notifySingleStudent.firstName }} {{ notifySingleStudent.lastName }}
        </div>

        <div class="afm-stu-email">
          <i class="bi bi-envelope me-1"></i>
          {{ notifySingleStudent.emailId || 'No email on record' }}
        </div>

        <div class="afm-stu-email" *ngIf="notifySingleStudent.mobileNo">
          <i class="bi bi-phone me-1"></i>
          {{ notifySingleStudent.mobileNo }}
        </div>
      </div>
    </div>

    <!-- Channel Selection -->
    <div class="afm-channel-section">
      <label class="afm-field-label">Send via</label>

      <div class="afm-channel-chips">
        <button
          class="afm-channel-chip afm-chip-email"
          [class.afm-channel-active]="notifyChannels.has('email')"
          (click)="toggleNotifyChannel('email')">
          <i class="bi bi-envelope-fill me-1"></i> Email
          <i
            class="bi bi-check-circle-fill ms-1 afm-chip-check"
            *ngIf="notifyChannels.has('email')">
          </i>
        </button>

        <button
          class="afm-channel-chip afm-chip-push"
          [class.afm-channel-active]="notifyChannels.has('pushNotification')"
          (click)="toggleNotifyChannel('pushNotification')">
          <i class="bi bi-bell-fill me-1"></i> Push Notification
          <i
            class="bi bi-check-circle-fill ms-1 afm-chip-check"
            *ngIf="notifyChannels.has('pushNotification')">
          </i>
        </button>

        <button
          class="afm-channel-chip afm-chip-sms"
          [class.afm-channel-active]="notifyChannels.has('sms')"
          (click)="toggleNotifyChannel('sms')">
          <i class="bi bi-chat-left-text-fill me-1"></i> SMS
          <i
            class="bi bi-check-circle-fill ms-1 afm-chip-check"
            *ngIf="notifyChannels.has('sms')">
          </i>
        </button>

        <button
          class="afm-channel-chip afm-chip-wa"
          disabled
          style="opacity:.45; cursor:not-allowed; pointer-events:none;">
          <i class="bi bi-whatsapp me-1"></i> WhatsApp
          <span class="afm-coming-soon-tag">Soon</span>
        </button>
      </div>
    </div>

    <!-- Send progress -->
    <div class="afm-send-progress" *ngIf="isSendingNotification">
      <div class="afm-sp-label">
        <span>Sending email…</span>
        <span class="afm-sp-pct">{{ bulkSendProgress }}%</span>
      </div>

      <div class="afm-sp-bar">
        <div class="afm-sp-fill" [style.width.%]="bulkSendProgress"></div>
      </div>
    </div>

    <!-- Info note -->
    <div class="afm-info-note">
      <i class="bi bi-info-circle-fill me-2"></i>
      A detailed fee reminder email with full payment summary will be sent to the parent's registered
      email address.
    </div>

  </div>

  <div class="modal-footer afm-modal-footer">
    <button
      class="universal-cancel-btn"
      (click)="modal.dismiss()"
      [disabled]="isSendingNotification">
      Cancel
    </button>

    <button
      class="universal-submit-btn"
      [disabled]="isSendingNotification || !notifyChannels.has('email')"
      (click)="sendNotification()">

      <span *ngIf="!isSendingNotification">
        <i class="bi bi-send me-1"></i> Send Email
      </span>

      <span *ngIf="isSendingNotification" class="afm-btn-sending">
        <div class="afm-btn-spinner"></div> Sending…
      </span>
    </button>
  </div>
</ng-template>

<!-- ═══════════════════════════════════════════════════
     BULK NOTIFY MODAL
════════════════════════════════════════════════════ -->
<ng-template #bulkNotifyModal let-modal>
  <div class="modal-header afm-modal-header">
    <h5 class="modal-title">
      <i class="bi bi-megaphone-fill me-2"></i>
      Bulk Fee Reminder — {{ selectedClass?.className }}
    </h5>
    <button
      type="button"
      class="btn-close btn-close-white"
      (click)="modal.dismiss()">
    </button>
  </div>

  <div class="modal-body afm-modal-body">

    <!-- Recipients summary -->
    <div class="afm-bulk-info">
      <i class="bi bi-people-fill me-2"></i>
Sending fee reminder to
      <strong>{{ bulkNotifyStudents.length }}</strong>
      student(s) with pending fees
      <span *ngIf="selectedSection">
        in section <strong>{{ selectedSection.sectionName }}</strong>
      </span>
      of <strong>{{ selectedClass?.className }}</strong>.
    </div>

    <!-- Channel Selection -->
    <div class="afm-channel-section">
      <label class="afm-field-label">Send via</label>

      <div class="afm-channel-chips">
        <button
          class="afm-channel-chip"
          [class.afm-channel-active]="notifyChannels.has('email')"
          (click)="toggleNotifyChannel('email')">
          <i class="bi bi-envelope-fill me-1"></i> Email
          <i
            class="bi bi-check-circle-fill ms-1 afm-chip-check"
            *ngIf="notifyChannels.has('email')">
          </i>
        </button>

        <button
          class="afm-channel-chip"
          disabled
          style="opacity:.45; cursor:not-allowed; pointer-events:none;">
          <i class="bi bi-chat-left-text-fill me-1"></i> SMS
          <span class="afm-coming-soon-tag">Soon</span>
        </button>

        <button
          class="afm-channel-chip afm-chip-wa"
          disabled
          style="opacity:.45; cursor:not-allowed; pointer-events:none;">
          <i class="bi bi-whatsapp me-1"></i> WhatsApp
          <span class="afm-coming-soon-tag">Soon</span>
        </button>
      </div>
    </div>

    <!-- Send progress -->
    <div class="afm-send-progress" *ngIf="isSendingNotification">
      <div class="afm-sp-label">
        <span>Sending emails one by one…</span>
        <span class="afm-sp-pct">{{ bulkSendProgress }}%</span>
      </div>

      <div class="afm-sp-bar">
        <div class="afm-sp-fill" [style.width.%]="bulkSendProgress"></div>
      </div>

      <div class="afm-sp-sub">
        {{ bulkSendSentCount }} of {{ bulkNotifyStudents.length }} sent
      </div>
    </div>

    <!-- Recipients list -->
    <div class="afm-bulk-list-header">
      <i class="bi bi-list-check me-1"></i>
      Recipients Preview
      <span class="afm-count-badge ms-2">{{ bulkNotifyStudents.length }}</span>
    </div>

    <div class="afm-bulk-list">
      <div
        *ngFor="let stu of bulkNotifyStudents; trackBy: trackByStudentId"
        class="afm-bulk-list-item">

        <div class="afm-avatar-xs afm-avatar-solid">
          {{ getStudentInitials(stu) }}
        </div>

        <div class="afm-bulk-stu-info">
          <span class="afm-bulk-stu-name">
            {{ stu.firstName }} {{ stu.lastName }}
          </span>
          <span class="afm-muted">
            {{ stu.emailId || stu.mobileNo || 'No contact info' }}
          </span>
        </div>

        <span class="afm-pending-badge">
          <i class="bi bi-clock-fill me-1"></i>Due
        </span>
      </div>

      <div *ngIf="bulkNotifyStudents.length === 0" class="afm-bulk-empty">
        <i class="bi bi-check-circle me-2"></i>
        No students with pending fees found.
      </div>
    </div>

  </div>

  <div class="modal-footer afm-modal-footer">
    <button
      class="afm-btn-cancel"
      (click)="modal.dismiss()"
      [disabled]="isSendingNotification">
      Cancel
    </button>

    <button
      class="afm-btn-send"
      [disabled]="isSendingNotification || bulkNotifyStudents.length === 0 || !notifyChannels.has('email')"
      (click)="sendNotification()">

      <span *ngIf="!isSendingNotification">
        <i class="bi bi-megaphone me-1"></i>
        Send to All ({{ bulkNotifyStudents.length }})
      </span>

      <span *ngIf="isSendingNotification" class="afm-btn-sending">
        <div class="afm-btn-spinner"></div>
        Sending {{ bulkSendProgress }}%…
      </span>
    </button>
  </div>
</ng-template>
